import { useTitle } from "../ai/parts";
import { Counter, DataTable, RowsSkeleton, SearchBox, useFilters } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { formatText, useTexts } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import { orgStatus, plural, type OrgRow } from "./organizations";
import styles from "./orgs.module.css";

/** Estado de la empresa: Activa en texto; Suspendida, En alta e Inactiva en su etiqueta (C15). */
export function OrgStatus({ status }: { status: string }) {
  const { L } = useTexts();
  const view = orgStatus(L, status);
  if (!view.tone) return <>{view.text}</>;
  return <span className={view.tone === "ochre" ? "tm-tag ochre" : "tm-tag"}>{view.text}</span>;
}

/**
 * `empresas` · Empresas (§5.2): buscador C06 (50 ms, en la dirección), contador sobre la tabla, tabla C28 Empresa ·
 * Código · Estado con la fila entera hacia el Resumen de la empresa, paginado C28 de 10 en el servidor (B3) y, en el
 * celular, lista C17 con carga al bajar. Vacío sin búsqueda con «Nueva empresa»; con búsqueda, «Limpiar búsqueda».
 */
export function OrganizationsPage() {
  const { L, locale } = useTexts();
  const { get, set } = useFilters();
  const search = get("search");
  useTitle(L("ADM_AREA_ORGS"));
  const list = useServerList<OrgRow>(
    `/api/admin/organizations${search ? `?search=${encodeURIComponent(search)}` : ""}`,
  );
  const head = (
    <PageHead title={L("ADM_AREA_ORGS")} sub={L("ADM_ORGS_SUB")}>
      <a className={`tm-btn primary ${ui.desktopInline}`} href="/Organizations/Crear">
        <ChatIcon name="plus" />
        {L("ADM_ORGS_NEW")}
      </a>
      <a className={ui.fab} href="/Organizations/Crear" aria-label={L("ADM_ORGS_NEW")} title={L("ADM_ORGS_NEW")}>
        <ChatIcon name="plus" />
      </a>
    </PageHead>
  );
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  return (
    <>
      {head}
      <div className={styles.search}>
        <SearchBox
          id="orgs-search"
          value={search}
          placeholder={L("ADM_ORGS_SEARCH")}
          onChange={(value) => set({ search: value })}
        />
      </div>
      {list.state === "loading" ? (
        <RowsSkeleton rows={10} table />
      ) : list.state === "error" || list.state === "notFound" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        search ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{formatText(L("ADM_ORGS_NO_MATCH"), search)}</strong>
            <button type="button" className="tm-btn secondary" onClick={() => set({ search: "" })}>
              {L("ADM_ORGS_CLEAR_SEARCH")}
            </button>
          </div>
        ) : (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_ORGS_EMPTY")}</strong>
            <a className="tm-btn primary" href="/Organizations/Crear">
              {L("ADM_ORGS_NEW")}
            </a>
          </div>
        )
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_ORGS_COUNT_ONE", "ADM_ORGS_COUNT")}</Counter>
          <DataTable
            label={L("ADM_AREA_ORGS")}
            rows={list.items}
            rowKey={(row) => row.organizationId}
            href={(row) => `/Organizations/Detail?id=${row.organizationId}`}
            columns={[
              {
                key: "name",
                title: L("ADM_ORGS_COL_COMPANY"),
                render: (row) => <strong className={ui.cellTitle}>{row.name}</strong>,
              },
              { key: "code", title: L("ADM_ORGS_COL_CODE"), render: (row) => row.code },
              { key: "status", title: L("ADM_ORGS_COL_STATUS"), render: (row) => <OrgStatus status={row.status} /> },
            ]}
            mobile={(row) => ({
              title: row.name,
              tag: row.status === "ACTIVE" ? undefined : <OrgStatus status={row.status} />,
              line: row.code,
            })}
          />
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}
